import { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { 
  Map, Layers, TrendingUp, Wind, Thermometer, Droplets,
  Cloud, Gauge, Eye, RefreshCw, Info, Maximize2,
  BarChart3, Activity, Circle
} from "lucide-react";
import { Button } from "./ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdvancedVisualizations({ token, lat, lon, isDarkMode }) {
  const [visualType, setVisualType] = useState("pressure");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showLegend, setShowLegend] = useState(true);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (lat && lon) {
      fetchVisualizationData();
    }
  }, [lat, lon, visualType]);

  const fetchVisualizationData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/weather/visualizations`, {
        params: { lat, lon, type: visualType },
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setData(response.data);
    } catch (error) {
      console.error("Failed to fetch visualization data:", error);
      // Generate mock data for demonstration
      setData(generateMockData(visualType));
    } finally {
      setLoading(false);
    }
  };

  const generateMockData = (type) => {
    const baseData = {
      type,
      timestamp: new Date().toISOString(),
      center: { lat, lon },
      grid_size: 5
    };

    switch (type) {
      case "pressure":
        return {
          ...baseData,
          title: "Pressure Isobars",
          unit: "hPa",
          isobars: [
            { value: 1020, label: "H", type: "high" },
            { value: 1015, label: "", type: "neutral" },
            { value: 1010, label: "", type: "neutral" },
            { value: 1005, label: "L", type: "low" }
          ],
          current_pressure: 1013,
          trend: "rising",
          fronts: [
            { type: "cold", direction: "SE", speed: 25 },
            { type: "warm", direction: "NE", speed: 15 }
          ]
        };
      case "temperature":
        return {
          ...baseData,
          title: "Temperature Isotherms",
          unit: "°F",
          isotherms: [
            { value: 80, color: "#ef4444" },
            { value: 70, color: "#f97316" },
            { value: 60, color: "#eab308" },
            { value: 50, color: "#22c55e" },
            { value: 40, color: "#3b82f6" },
            { value: 30, color: "#6366f1" }
          ],
          current_temp: 65,
          gradient: "NW-SE",
          max_temp: { value: 78, location: "South" },
          min_temp: { value: 52, location: "North" }
        };
      case "wind":
        return {
          ...baseData,
          title: "Wind Streamlines",
          unit: "mph",
          streamlines: [
            { direction: 45, speed: 15, convergence: false },
            { direction: 60, speed: 20, convergence: false },
            { direction: 90, speed: 25, convergence: true }
          ],
          vorticity_zones: [
            { lat: lat + 0.5, lon: lon - 0.3, intensity: "moderate" }
          ],
          max_wind: { speed: 35, gust: 45, direction: "SW" },
          convergence_zones: 1
        };
      case "humidity":
        return {
          ...baseData,
          title: "Humidity Isohumes",
          unit: "%",
          isohumes: [
            { value: 90, color: "#1e40af" },
            { value: 75, color: "#3b82f6" },
            { value: 60, color: "#60a5fa" },
            { value: 45, color: "#93c5fd" },
            { value: 30, color: "#dbeafe" }
          ],
          current_humidity: 68,
          dew_point: 55,
          moisture_advection: "positive"
        };
      case "precipitation":
        return {
          ...baseData,
          title: "Precipitation Isohyets",
          unit: "in",
          isohyets: [
            { value: 2.0, intensity: "heavy" },
            { value: 1.0, intensity: "moderate" },
            { value: 0.5, intensity: "light" },
            { value: 0.1, intensity: "trace" }
          ],
          total_24h: 0.75,
          precip_type: "rain",
          coverage: "scattered"
        };
      default:
        return baseData;
    }
  };

  const visualTypes = [
    { id: "pressure", label: "Pressure (Isobars)", icon: Gauge },
    { id: "temperature", label: "Temperature (Isotherms)", icon: Thermometer },
    { id: "wind", label: "Wind (Streamlines)", icon: Wind },
    { id: "humidity", label: "Humidity (Isohumes)", icon: Droplets },
    { id: "precipitation", label: "Precipitation (Isohyets)", icon: Cloud }
  ];

  const renderVisualization = () => {
    if (!data) return null;

    switch (visualType) {
      case "pressure":
        return (
          <div className="relative h-64 rounded-xl overflow-hidden bg-gradient-to-br from-blue-900/50 to-purple-900/50">
            {/* Isobar Lines */}
            <svg className="absolute inset-0 w-full h-full" viewBox="0 0 400 256">
              {data.isobars?.map((isobar, idx) => (
                <g key={`item-${idx}`}>
                  <path
                    d={`M ${20 + idx * 30} 20 Q ${200} ${80 + idx * 40} ${380 - idx * 30} ${256 - idx * 30}`}
                    fill="none"
                    stroke={isobar.type === "high" ? "#22c55e" : isobar.type === "low" ? "#ef4444" : "#94a3b8"}
                    strokeWidth="2"
                    strokeDasharray={isobar.type === "neutral" ? "5,5" : "none"}
                  />
                  <text
                    x={20 + idx * 30}
                    y={15}
                    fill="white"
                    fontSize="10"
                    className="font-mono"
                  >
                    {isobar.value}
                  </text>
                  {isobar.label && (
                    <circle
                      cx={200 + (isobar.type === "high" ? -80 : 80)}
                      cy={128}
                      r="20"
                      fill={isobar.type === "high" ? "#22c55e33" : "#ef444433"}
                      stroke={isobar.type === "high" ? "#22c55e" : "#ef4444"}
                      strokeWidth="2"
                    />
                  )}
                  {isobar.label && (
                    <text
                      x={200 + (isobar.type === "high" ? -80 : 80)}
                      y={133}
                      fill="white"
                      fontSize="16"
                      fontWeight="bold"
                      textAnchor="middle"
                    >
                      {isobar.label}
                    </text>
                  )}
                </g>
              ))}
              {/* Fronts */}
              {data.fronts?.map((front, idx) => (
                <g key={`front-${idx}`}>
                  <path
                    d={`M ${50 + idx * 150} 200 L ${150 + idx * 150} 150`}
                    fill="none"
                    stroke={front.type === "cold" ? "#3b82f6" : "#ef4444"}
                    strokeWidth="4"
                  />
                  {[0, 1, 2, 3].map(i => (
                    <polygon
                      key={i}
                      points={front.type === "cold" 
                        ? `${70 + idx * 150 + i * 25},${190 - i * 12} ${80 + idx * 150 + i * 25},${175 - i * 12} ${90 + idx * 150 + i * 25},${190 - i * 12}`
                        : `${70 + idx * 150 + i * 25},${190 - i * 12} ${80 + idx * 150 + i * 25},${205 - i * 12} ${90 + idx * 150 + i * 25},${190 - i * 12}`
                      }
                      fill={front.type === "cold" ? "#3b82f6" : "#ef4444"}
                    />
                  ))}
                </g>
              ))}
            </svg>
            {/* Current Pressure Display */}
            <div className="absolute top-4 right-4 bg-black/50 backdrop-blur-sm rounded-lg px-3 py-2">
              <p className="text-xs opacity-60">Current</p>
              <p className="text-xl font-bold">{data.current_pressure} hPa</p>
              <p className={`text-xs ${data.trend === "rising" ? "text-green-400" : "text-red-400"}`}>
                {data.trend === "rising" ? "↑ Rising" : "↓ Falling"}
              </p>
            </div>
          </div>
        );

      case "temperature":
        return (
          <div className="relative h-64 rounded-xl overflow-hidden">
            {/* Temperature Gradient Background */}
            <div className="absolute inset-0 bg-gradient-to-br from-red-500/30 via-yellow-500/30 to-blue-500/30" />
            {/* Isotherms */}
            <svg className="absolute inset-0 w-full h-full" viewBox="0 0 400 256">
              {data.isotherms?.map((iso, idx) => (
                <g key={`item-${idx}`}>
                  <path
                    d={`M 0 ${40 + idx * 40} Q ${200} ${20 + idx * 45} ${400} ${50 + idx * 38}`}
                    fill="none"
                    stroke={iso.color}
                    strokeWidth="3"
                    opacity="0.8"
                  />
                  <rect
                    x="10"
                    y={35 + idx * 40}
                    width="35"
                    height="18"
                    fill={iso.color}
                    rx="4"
                  />
                  <text
                    x="27"
                    y={48 + idx * 40}
                    fill="white"
                    fontSize="11"
                    fontWeight="bold"
                    textAnchor="middle"
                  >
                    {iso.value}°
                  </text>
                </g>
              ))}
            </svg>
            {/* Temperature Info */}
            <div className="absolute top-4 right-4 bg-black/50 backdrop-blur-sm rounded-lg px-3 py-2">
              <p className="text-xs opacity-60">Current</p>
              <p className="text-2xl font-bold">{data.current_temp}°F</p>
              <div className="flex gap-3 mt-1 text-xs">
                <span className="text-red-400">H: {data.max_temp?.value}°</span>
                <span className="text-blue-400">L: {data.min_temp?.value}°</span>
              </div>
            </div>
          </div>
        );

      case "wind":
        return (
          <div className="relative h-64 rounded-xl overflow-hidden bg-gradient-to-br from-slate-800 to-slate-900">
            {/* Wind Streamlines */}
            <svg className="absolute inset-0 w-full h-full" viewBox="0 0 400 256">
              {/* Animated streamlines */}
              {[...Array(12)].map((_, idx) => (
                <g key={`item-${idx}`}>
                  <path
                    d={`M ${-50 + (idx % 4) * 120} ${20 + Math.floor(idx / 4) * 80} 
                       Q ${100 + (idx % 4) * 100} ${50 + Math.floor(idx / 4) * 70 + Math.sin(idx) * 20} 
                       ${450 + (idx % 4) * 50} ${30 + Math.floor(idx / 4) * 85}`}
                    fill="none"
                    stroke="#60a5fa"
                    strokeWidth="2"
                    opacity="0.6"
                    strokeDasharray="10,5"
                  >
                    <animate
                      attributeName="stroke-dashoffset"
                      from="0"
                      to="-30"
                      dur={`${1.5 + idx * 0.2}s`}
                      repeatCount="indefinite"
                    />
                  </path>
                  {/* Arrow heads */}
                  <polygon
                    points={`${200 + idx * 15},${100 + (idx % 3) * 40} ${210 + idx * 15},${95 + (idx % 3) * 40} ${210 + idx * 15},${105 + (idx % 3) * 40}`}
                    fill="#60a5fa"
                    opacity="0.8"
                  />
                </g>
              ))}
              {/* Convergence Zone */}
              {data.convergence_zones > 0 && (
                <g>
                  <circle cx="280" cy="150" r="30" fill="#ef444433" stroke="#ef4444" strokeWidth="2" strokeDasharray="5,3" />
                  <text x="280" y="155" fill="#ef4444" fontSize="10" textAnchor="middle">CONV</text>
                </g>
              )}
            </svg>
            {/* Wind Info */}
            <div className="absolute top-4 right-4 bg-black/50 backdrop-blur-sm rounded-lg px-3 py-2">
              <p className="text-xs opacity-60">Max Wind</p>
              <p className="text-xl font-bold">{data.max_wind?.speed} mph</p>
              <p className="text-xs text-orange-400">Gusts: {data.max_wind?.gust} mph</p>
              <p className="text-xs opacity-60 mt-1">From {data.max_wind?.direction}</p>
            </div>
          </div>
        );

      case "humidity":
        return (
          <div className="relative h-64 rounded-xl overflow-hidden bg-gradient-to-br from-blue-900/50 to-cyan-900/50">
            {/* Humidity Bands */}
            <svg className="absolute inset-0 w-full h-full" viewBox="0 0 400 256">
              {data.isohumes?.map((iso, idx) => (
                <g key={`item-${idx}`}>
                  <rect
                    x="0"
                    y={idx * 52}
                    width="400"
                    height="52"
                    fill={iso.color}
                    opacity="0.3"
                  />
                  <line
                    x1="0"
                    y1={idx * 52 + 26}
                    x2="400"
                    y2={idx * 52 + 26 + (idx % 2 === 0 ? 10 : -10)}
                    stroke={iso.color}
                    strokeWidth="2"
                  />
                  <text
                    x="15"
                    y={idx * 52 + 30}
                    fill="white"
                    fontSize="12"
                    fontWeight="bold"
                  >
                    {iso.value}%
                  </text>
                </g>
              ))}
            </svg>
            {/* Humidity Info */}
            <div className="absolute top-4 right-4 bg-black/50 backdrop-blur-sm rounded-lg px-3 py-2">
              <p className="text-xs opacity-60">Current RH</p>
              <p className="text-2xl font-bold">{data.current_humidity}%</p>
              <p className="text-xs opacity-60 mt-1">Dew Point: {data.dew_point}°F</p>
              <p className={`text-xs mt-1 ${data.moisture_advection === "positive" ? "text-blue-400" : "text-orange-400"}`}>
                Moisture: {data.moisture_advection === "positive" ? "↑ Increasing" : "↓ Decreasing"}
              </p>
            </div>
          </div>
        );

      case "precipitation":
        return (
          <div className="relative h-64 rounded-xl overflow-hidden bg-gradient-to-br from-slate-800 to-blue-900">
            {/* Precipitation Areas */}
            <svg className="absolute inset-0 w-full h-full" viewBox="0 0 400 256">
              {data.isohyets?.map((iso, idx) => (
                <g key={`item-${idx}`}>
                  <ellipse
                    cx="200"
                    cy="128"
                    rx={180 - idx * 40}
                    ry={120 - idx * 25}
                    fill="none"
                    stroke={iso.intensity === "heavy" ? "#1e40af" : iso.intensity === "moderate" ? "#3b82f6" : iso.intensity === "light" ? "#60a5fa" : "#93c5fd"}
                    strokeWidth="2"
                    strokeDasharray={iso.intensity === "trace" ? "5,5" : "none"}
                    opacity="0.7"
                  />
                  <text
                    x={380 - idx * 40}
                    y={128 + idx * 10}
                    fill="white"
                    fontSize="10"
                    className="font-mono"
                  >
                    {iso.value}&quot;
                  </text>
                </g>
              ))}
              {/* Rain drops animation */}
              {[...Array(20)].map((_, idx) => (
                <circle
                  key={`item-${idx}`}
                  cx={50 + idx * 20}
                  cy={0}
                  r="2"
                  fill="#60a5fa"
                >
                  <animate
                    attributeName="cy"
                    from="-10"
                    to="270"
                    dur={`${1 + Math.random()}s`}
                    repeatCount="indefinite"
                    begin={`${Math.random() * 2}s`}
                  />
                  <animate
                    attributeName="opacity"
                    from="0.8"
                    to="0"
                    dur={`${1 + Math.random()}s`}
                    repeatCount="indefinite"
                    begin={`${Math.random() * 2}s`}
                  />
                </circle>
              ))}
            </svg>
            {/* Precip Info */}
            <div className="absolute top-4 right-4 bg-black/50 backdrop-blur-sm rounded-lg px-3 py-2">
              <p className="text-xs opacity-60">24h Total</p>
              <p className="text-2xl font-bold">{data.total_24h}&quot;</p>
              <p className="text-xs opacity-60 mt-1">Type: {data.precip_type}</p>
              <p className="text-xs text-blue-400">Coverage: {data.coverage}</p>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-xl ${isDarkMode ? 'bg-indigo-500/20' : 'bg-indigo-100'}`}>
            <BarChart3 className="w-5 h-5 text-indigo-400" />
          </div>
          <div>
            <h3 className="font-semibold">Advanced Weather Visualizations</h3>
            <p className="text-xs opacity-60">Professional meteorological analysis</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowLegend(!showLegend)}
            className="text-xs"
          >
            <Info className="w-4 h-4 mr-1" />
            Legend
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={fetchVisualizationData}
            disabled={loading}
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {/* Visualization Type Selector */}
      <div className="flex flex-wrap gap-2">
        {visualTypes.map(type => {
          const Icon = type.icon;
          return (
            <Button
              key={type.id}
              variant={visualType === type.id ? "default" : "outline"}
              size="sm"
              onClick={() => setVisualType(type.id)}
              className={visualType === type.id ? "bg-indigo-500 hover:bg-indigo-600" : ""}
            >
              <Icon className="w-4 h-4 mr-1" />
              {type.label.split(" ")[0]}
            </Button>
          );
        })}  
      </div>

      {/* Visualization Display */}
      {loading ? (
        <div className="h-64 flex items-center justify-center bg-black/20 rounded-xl">
          <RefreshCw className="w-8 h-8 animate-spin opacity-50" />
        </div>
      ) : (
        <motion.div
          key={visualType}
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.3 }}
        >
          {renderVisualization()}
        </motion.div>
      )}

      {/* Legend */}
      {showLegend && data && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          className={`p-4 rounded-xl ${isDarkMode ? 'bg-white/5' : 'bg-black/5'}`}
        >
          <h4 className="font-semibold text-sm mb-2">Understanding {data.title}</h4>
          <div className="grid grid-cols-2 gap-2 text-xs opacity-80">
            {visualType === "pressure" && (
              <>
                <div className="flex items-center gap-2">
                  <Circle className="w-3 h-3 text-green-400 fill-green-400" />
                  <span>High Pressure (H) - Fair weather</span>
                </div>
                <div className="flex items-center gap-2">
                  <Circle className="w-3 h-3 text-red-400 fill-red-400" />
                  <span>Low Pressure (L) - Unsettled weather</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-6 h-0.5 bg-blue-500" />
                  <span>Cold Front - Cooler air advancing</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-6 h-0.5 bg-red-500" />
                  <span>Warm Front - Warmer air advancing</span>
                </div>
              </>
            )}
            {visualType === "temperature" && (
              <>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded bg-red-500" />
                  <span>Hot (80°F+)</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded bg-yellow-500" />
                  <span>Warm (60-70°F)</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded bg-green-500" />
                  <span>Mild (50-60°F)</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded bg-blue-500" />
                  <span>Cool (Below 50°F)</span>
                </div>
              </>
            )}
            {visualType === "wind" && (
              <>
                <div className="flex items-center gap-2">
                  <Activity className="w-4 h-4 text-blue-400" />
                  <span>Streamlines show wind flow direction</span>
                </div>
                <div className="flex items-center gap-2">
                  <Circle className="w-3 h-3 text-red-400" />
                  <span>CONV - Convergence zone (storm potential)</span>
                </div>
              </>
            )}
            {visualType === "humidity" && (
              <>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded bg-blue-800" />
                  <span>Very Humid (90%+)</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded bg-blue-400" />
                  <span>Moderate (60-75%)</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded bg-blue-200" />
                  <span>Dry (Below 45%)</span>
                </div>
              </>
            )}
            {visualType === "precipitation" && (
              <>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded bg-blue-800" />
                  <span>Heavy (2&quot;+)</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded bg-blue-500" />
                  <span>Moderate (1&quot;)</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded bg-blue-300" />
                  <span>Light (0.5&quot;)</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded border border-blue-200" />
                  <span>Trace (0.1&quot;)</span>
                </div>
              </>
            )}
          </div>
        </motion.div>
      )}
    </div>
  );
}
